// Branching wrapper for /login + its sub-states.
//
// The `useAuthFlow` state machine (packages/consumer/src/auth/useAuthFlow.ts)
// drives WHICH auth screen renders based on `status.kind`, so there's no
// need for separate routes per state — react-router mounts this at /login
// and the component switches internally. Same pattern as the admin SPA
// (packages/admin/src/routes/AuthRoutes.tsx).
//
// Consumer pool MFA is OPTIONAL (per the Cognito module default), so the
// mfa-* states are not expected to fire for admin-created invites. If they
// do (a tenant-admin opts in later, say), we render a safe placeholder
// rather than crash; the full MFAChallengeScreen + MFASetupWizard are a
// Wave-2 port.

import type { JSX } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../auth/index.js';
import { useI18n } from '../../i18n/index.js';
import { ConsumerAuthShell } from '../../auth/index.js';
import { LoginScreen } from './LoginScreen.js';
import { FirstLoginPasswordScreen } from './FirstLoginPasswordScreen.js';
import styles from './AuthScreens.module.css';

export function AuthRoutes(): JSX.Element | null {
  const { status } = useAuth();

  switch (status.kind) {
    case 'idle':
      return null;
    case 'authenticated':
      return <Navigate to="/" replace />;
    case 'new-password-required':
    case 'submitting-new-password':
      return <FirstLoginPasswordScreen />;
    case 'mfa-challenge':
    case 'submitting-mfa':
    case 'mfa-setup-required':
    case 'mfa-setup-pending':
    case 'submitting-mfa-verify':
      return <MfaPlaceholder />;
    case 'unauthenticated':
    case 'submitting-login':
    default:
      return <LoginScreen />;
  }
}

/**
 * Safe placeholder for MFA states on consumer side. Consumer pool MFA is
 * OPTIONAL today, so these states shouldn't fire for admin-created tenant
 * invites. If a tenant-admin later opts in to MFA and we haven't shipped
 * the full flow yet, this screen keeps them unstuck with a path to support
 * rather than silent breakage.
 */
function MfaPlaceholder(): JSX.Element {
  const { t } = useI18n();
  return (
    <ConsumerAuthShell>
      <div className={styles.formColumn}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>{t('mfaPlaceholder.eyebrow')}</span>
          <h1 className={styles.title}>{t('mfaPlaceholder.headline')}</h1>
          <p className={styles.body}>{t('mfaPlaceholder.body')}</p>
        </header>
      </div>
    </ConsumerAuthShell>
  );
}
